import { useState } from 'react';
import type { FormEvent } from 'react';
import type {
  ContactListItem,
  SourceContact,
  TerritoireContact,
  TypeClient,
} from '@cockpit/shared';
import { SourcesContact, TerritoiresContact, TypesClient } from '@cockpit/shared';
import { api } from '../../lib/api';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { Select } from '../ui/Select';
import {
  LABEL_SOURCE_CONTACT,
  LABEL_TERRITOIRE_CONTACT,
  LABEL_TYPE_CLIENT,
} from '../../lib/format';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  initialName: string;
  defaultTerritoire?: TerritoireContact;
  onCreated: (contact: ContactListItem) => void;
}

export function ContactLegerModal({
  isOpen,
  onClose,
  initialName,
  defaultTerritoire,
  onCreated,
}: Props): JSX.Element {
  const [nom, setNom] = useState(initialName);
  const [societe, setSociete] = useState('');
  const [typeClient, setTypeClient] = useState<TypeClient>('Paysagiste');
  const [territoire, setTerritoire] = useState<TerritoireContact>(defaultTerritoire ?? 'Les_deux');
  const [source, setSource] = useState<SourceContact>('Autre');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    api.contacts
      .createLeger({
        nom,
        societe: societe.trim() === '' ? null : societe.trim(),
        type_client: typeClient,
        territoire,
        source,
      })
      .then(({ contact }) => {
        onCreated({
          id: contact.id,
          nom: contact.nom,
          prenom: null,
          societe: contact.societe,
          type_client: contact.type_client,
          territoire: contact.territoire,
          statut_pipeline: contact.statut_pipeline,
          departement: null,
          email: null,
          telephone: null,
          prochaine_relance: null,
          date_derniere_interaction: null,
          valeur_estimee_centimes: 0,
          tags: [],
        });
        onClose();
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Erreur création contact');
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Créer un contact léger"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Annuler
          </Button>
          <Button form="form-contact-leger" type="submit" disabled={submitting}>
            {submitting ? 'Création…' : 'Créer'}
          </Button>
        </>
      }
    >
      <form id="form-contact-leger" onSubmit={handleSubmit} className="flex flex-col gap-3">
        <Input
          label="Nom"
          value={nom}
          onChange={(e): void => setNom(e.target.value)}
          required
          autoFocus
        />
        <Input
          label="Société (optionnel)"
          value={societe}
          onChange={(e): void => setSociete(e.target.value)}
        />
        <Select
          label="Type de client"
          value={typeClient}
          onChange={(e): void => setTypeClient(e.target.value as TypeClient)}
          options={TypesClient.map((t) => ({ value: t, label: LABEL_TYPE_CLIENT[t] ?? t }))}
        />
        <Select
          label="Territoire"
          value={territoire}
          onChange={(e): void => setTerritoire(e.target.value as TerritoireContact)}
          options={TerritoiresContact.map((t) => ({
            value: t,
            label: LABEL_TERRITOIRE_CONTACT[t] ?? t,
          }))}
        />
        <Select
          label="Source"
          value={source}
          onChange={(e): void => setSource(e.target.value as SourceContact)}
          options={SourcesContact.map((s) => ({ value: s, label: LABEL_SOURCE_CONTACT[s] ?? s }))}
        />
        {error !== null && <p className="text-xs text-red-400">{error}</p>}
        <p className="text-xs text-slate-500">
          Le statut "Gagné" sera attribué automatiquement (un contact saisi via vente est déjà
          client).
        </p>
      </form>
    </Modal>
  );
}
